import React from 'react'
import { StyledComponent } from '@emotion/styled'
import ContentfulImage, { ContentfulImageProps } from 'experiences/shared/ContentfulImage'
import { CoverArticleCard, ResponsiveArticleCard, SmallCard, StandardArticleCard } from './styles/Variants.style'
import { FavoriteButton } from '../FavoriteButton/FavoriteButton'
import { TextLineSkeleton } from 'components/patterns/Skeleton'
import { SkeletonImage } from './styles/Base.style'
import {
	ArticleCardBlocks,
	ArticleCardMode,
	PendingProps,
	RequiredProps
} from 'experiences/learn/articles/ArticleCard/types'
import { Pending } from 'components/patterns/suspense/internals/types'

const IMAGE_HEIGHT = 400
const IMAGE_WIDTH = 711

const largeSizes = '(min-width: 1366px) 528px, (min-width: 1023px) 30vw, (min-width: 560px) 40vw, 50vw'
const smallSizes = '(min-width: 1023px) 400px, (min-width: 560px) 30vw, 50vw'

interface CardConfig {
	component: StyledComponent<any>
	imageSizes: string
	imageProps?: Partial<ContentfulImageProps>
}

const CardLayoutConfigs: { [x in ArticleCardMode]: CardConfig } = {
	mini: {
		component: SmallCard,
		imageSizes: smallSizes,
		imageProps: { width: 300 }
	},
	standard: { component: StandardArticleCard, imageSizes: smallSizes },
	cover: {
		component: CoverArticleCard,
		imageSizes: largeSizes,
		imageProps: { layout: 'fill' }
	},
	responsive: { component: ResponsiveArticleCard, imageSizes: largeSizes }
}

export const buildArticleBlocks = (props: Pending<PendingProps, RequiredProps>): ArticleCardBlocks => {
	const mode = props.mode
	const RootComponent = CardLayoutConfigs[mode].component

	if (props.pending) {
		return {
			image: <SkeletonImage style={{ paddingBottom: `${(IMAGE_HEIGHT / IMAGE_WIDTH) * 100}%` }} />,
			topic: <TextLineSkeleton width={200} />,
			title: <TextLineSkeleton />,
			description: (
				<>
					<TextLineSkeleton />
					<TextLineSkeleton />
					<TextLineSkeleton />
					<TextLineSkeleton />
				</>
			),
			readingTime: <TextLineSkeleton width={75} />,
			favoriteIcon: <div />,
			RootComponent,
			href: { href: '#' }
		}
	} else {
		const article = props.article
		return {
			image: (
				<ContentfulImage
					asset={article.primaryImage}
					layout="fill"
					sizes="25vw"
					objectPosition="50% 20%"
					objectFit={'cover'}
					placeholder={true}
					quality={50}
					{...CardLayoutConfigs[mode].imageProps}
				/>
			),
			topic: <>{article.category}</>,
			title: <>{article.title}</>,
			description: <>{article.description}</>,
			readingTime: <>{article.readingTime}</>,
			favoriteIcon: <FavoriteButton id={article.id} />,
			RootComponent,
			href: { pathname: article.relativePath }
		}
	}
}
